import R from 'ramda';

import { WeeklyTimeSpan } from 'apis/restaurant-api/merchant-admin/menu2/types';

import {
  groupByWeekday,
  mergeContiguousTimePairsWithinEntries,
  groupByTimePairs,
  floorTimePairsToMinutes,
  rawEntriesToWeeklyScheduleEntries,
  RawEntry,
  splitWeeklyTimePairAtMidnights,
} from './fromWeeklyScheduleToWeeklyScheduleInputEntries';
import { WeeklyScheduleInputEntry } from './types';

// Separate type to make transforms very explicit
// Essentially same as WeeklyTimeSpan, but times are in milliseconds instead of minutes
type WeeklyTimeSpanInMillis = {
  end: number; // Milliseconds since Monday 0:00. Integer.
  start: number; // Milliseconds since Monday 0:00. Integer.
};

const MINUTE_DURATION_MS = 60 * 1000;
const DAY_DURATION_MS = 24 * 60 * MINUTE_DURATION_MS;
const WEEK_DURATION_MS = 7 * DAY_DURATION_MS;

const DAY_DURATION_MIN = 24 * 60;
const WEEK_DURATION_MIN = 7 * DAY_DURATION_MIN;

const minutesToMilliseconds = (minutes: number) => {
  return minutes * 60 * 1000;
};

export const transformTimeSpanMinutesToMilliseconds = (timeSpans: WeeklyTimeSpan[]): WeeklyTimeSpanInMillis[] => {
  return timeSpans.map(timeSpan => ({
    start: minutesToMilliseconds(timeSpan.start),
    end: minutesToMilliseconds(timeSpan.end),
  }));
};

export const checkForSundayMondayOverlap = (timeSpans: WeeklyTimeSpan[]): WeeklyTimeSpan[] => {
  if (timeSpans[0].start === 0 && timeSpans[timeSpans.length - 1].end === WEEK_DURATION_MIN) {
    const firstTimeSpanLength = timeSpans[0].end - timeSpans[0].start;
    const lastTimeSpanLength = timeSpans[timeSpans.length - 1].end - timeSpans[timeSpans.length - 1].start;

    // If Mon-Sun overlapping timespan is shorter or exactly 24 hours
    // Combine to one timespan for Sunday
    if (firstTimeSpanLength + lastTimeSpanLength <= DAY_DURATION_MIN) {
      const mergedLastTimeSpan = {
        ...timeSpans[timeSpans.length - 1],
        end: timeSpans[timeSpans.length - 1].end + timeSpans[0].end,
      };

      const adjustedTimeSpans = [...timeSpans];

      adjustedTimeSpans.shift();
      adjustedTimeSpans.pop();
      adjustedTimeSpans.push(mergedLastTimeSpan);

      return adjustedTimeSpans;
    }
  }

  return timeSpans;
};

const fromWeeklyTimeSpansToRawEntries = (timeSpans: WeeklyTimeSpan[]): RawEntry[] => {
  const adjustedTimeSpans = checkForSundayMondayOverlap(timeSpans);
  const transformedTimeSpans = transformTimeSpanMinutesToMilliseconds(adjustedTimeSpans);

  const flattenedEvents = transformedTimeSpans
    .map(timePair => [
      {
        type: 'start',
        value: timePair.start,
      },
      {
        type: 'end',
        value: timePair.end,
      },
    ])
    .flat();

  const startEvents = flattenedEvents.filter(event => event.type === 'start').map(event => event.value);
  const endEvents = flattenedEvents.filter(event => event.type === 'end').map(event => event.value);

  const weeklyTimePairs = startEvents
    .map(startTime => {
      const nextEndTimes = endEvents
        .map(endTimes => (endTimes > startTime ? endTimes : endTimes + WEEK_DURATION_MS))
        .sort((a, b) => a - b);
      const [nextEndTime] = nextEndTimes;
      return {
        start: startTime,
        end: nextEndTime % WEEK_DURATION_MS,
      };
    })
    .flatMap(splitWeeklyTimePairAtMidnights);

  return weeklyTimePairs.map(weeklyTimePair => {
    const weekdayIndex = Math.floor(weeklyTimePair.start / DAY_DURATION_MS);
    return {
      weekdayIndices: [weekdayIndex],
      timePairs: [
        {
          start: weeklyTimePair.start - weekdayIndex * DAY_DURATION_MS,
          end: weeklyTimePair.end % DAY_DURATION_MS,
        },
      ],
    };
  });
};

const fromWeeklyTimeSpansToWeeklyScheduleInputEntries: (timeSpans: WeeklyTimeSpan[]) => WeeklyScheduleInputEntry[] =
  R.pipe(
    fromWeeklyTimeSpansToRawEntries,
    groupByWeekday,
    mergeContiguousTimePairsWithinEntries,
    groupByTimePairs,
    floorTimePairsToMinutes,
    rawEntriesToWeeklyScheduleEntries,
  );

export default fromWeeklyTimeSpansToWeeklyScheduleInputEntries;
